import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";

import type { TyreDiscount } from "models";
import type { AddUpdateTyreDiscountFormValues } from "modules/TyreDiscountsDashboard/components";

type AddUpdateTyreDiscountFormProps = {
  hasErrors: boolean;
  isPending: boolean;
  tyreDiscount: TyreDiscount | null;
  onClose: () => void;
  onSubmit: (values: AddUpdateTyreDiscountFormValues) => void;
};

const FormHelper = createFormHelper<AddUpdateTyreDiscountFormValues>();

export const AddUpdateTyreDiscountForm = ({ hasErrors, isPending, onClose, onSubmit, tyreDiscount }: AddUpdateTyreDiscountFormProps) => {
  const { t } = useTranslation();
  const { watch, handleSubmit, setValue } = FormHelper.useFormContext();

  const [name, discount, netMarginEuro, netMarginPercent, isBrandDiscountOnly] = watch([
    "name",
    "discount",
    "net_margin_euro",
    "net_margin_percent",
    "is_brand_discount_only"
  ]);

  const handleBrandDiscountOnlyChange = (isBrandDiscountOnly: boolean) => {
    if (!isBrandDiscountOnly) return;

    setValue("discount", null);
    setValue("net_margin_euro", null);
    setValue("net_margin_percent", null);
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-container">
          <div className="header-title">{!tyreDiscount ? t("v8_add_discount", "Add Discount") : t("v8_edit_discount", "Edit Discount")}</div>
          <div className="header-checkbox-container">
            <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
          </div>
        </div>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!name && hasErrors}>
              <label>{t("v8_discount_name", "Discount Name")} </label>
              <FormHelper.Field.Input name="name" inputProps={{ placeholder: t("v8_name", "Name"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required>
              <FormHelper.Field.Checkbox
                name="is_brand_discount_only"
                onChange={value => handleBrandDiscountOnlyChange(value)}
                label={t("v8_is_brand_discount_only", "Is brand discount only")}
                checkboxProps={{ toggle: true }}
              />
            </Form.Field>
          </GridColumn>
        </Grid>
        {!isBrandDiscountOnly && (
          <Grid columns="equal">
            <GridColumn>
              <Form.Field required error={(!discount || discount > 99.99 || discount < 0) && hasErrors}>
                <label>{t("v8_discount", "Discount")}</label>
                <FormHelper.Field.Input
                  name="discount"
                  nullable
                  inputProps={{
                    disabled: netMarginEuro != null || netMarginPercent != null,
                    placeholder: t("v8_discount", "Discount"),
                    fluid: true,
                    labelPosition: "right",
                    label: { basic: true, content: <Icon name="percent" /> },
                    type: "number"
                  }}
                />
              </Form.Field>
            </GridColumn>
            <GridColumn>
              <Form.Field required error={(!netMarginEuro || netMarginEuro > 99.99 || netMarginEuro < 0) && hasErrors}>
                <label>{`${t("v8_net", "Net")} + ${t("v8_margin", "Margin")}`}</label>
                <FormHelper.Field.Input
                  name="net_margin_euro"
                  nullable
                  inputProps={{
                    disabled: discount != null || netMarginPercent != null,
                    placeholder: t("v8_discount", "Discount"),
                    fluid: true,
                    labelPosition: "right",
                    label: { basic: true, content: <Icon name="euro" /> },
                    type: "number"
                  }}
                />
              </Form.Field>
            </GridColumn>
            <GridColumn>
              <Form.Field required error={(!netMarginPercent || netMarginPercent > 99.99 || netMarginPercent < 0) && hasErrors}>
                <label>{`${t("v8_net", "Net")} + ${t("v8_percent", "Percent")}`}</label>
                <FormHelper.Field.Input
                  name="net_margin_percent"
                  nullable
                  inputProps={{
                    disabled: discount != null || netMarginEuro != null,
                    placeholder: t("v8_discount", "Discount"),
                    fluid: true,
                    labelPosition: "right",
                    label: { basic: true, content: <Icon name="percent" /> },
                    type: "number"
                  }}
                />
              </Form.Field>
            </GridColumn>
          </Grid>
        )}
      </Modal.Content>
      <Divider fitted />

      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending} onClick={handleSubmit(d => onSubmit(d))}>
              {!tyreDiscount ? t("v8_save", "Save") : t("v8_update", "Update")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
